// src/pages/projects/MOMDirectoryPage.jsx
// Directory of all Minutes of Meeting documents for a project, organised by stage.
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { FileText, ChevronDown, ChevronRight, Plus, Send, Search, Trash2 } from 'lucide-react';
import { useMOMData } from '../../hooks/useMOMData';
import { MOMPanel } from './TimelinesPage';
import { useSettings } from '../../hooks/useSettings';
import { resolveBrandingImageSrc } from '../../utils/brandingImages';

// ── Design tokens (mirrors TimelinesPage) ────────────────────────────────────
const BRIO_GOLD   = '#9F902A';
const BRIO_BORDER = '#e2e8f0';
const BRIO_GREY   = '#6b7280';
const BRIO_BG     = '#faf9f7';

// ── Stage name map (mirrors TimelinesPage) ───────────────────────────────────
const STAGE_NAMES = {
  pcd:  'PCD',
  advance: 'Advance',
  cd_ad: 'CD AD',  cd_id: 'CD ID',
  sd_ad: 'SD AD',  sd_id: 'SD ID',
  dd_ad: 'DD AD',  dd_id: 'DD ID',
  aoc_ad: 'AoC AD', aoc_id: 'AoC ID',
  td_ad: 'TD AD',  td_id: 'TD ID',
  gfc1: 'GFC 1',   gfc2: 'GFC 2', gfc3: 'GFC 3',
  completion: 'Completion', cc: 'CC',
};

// Default task-id → timeline-stage-key map (mirrors TimelinesPage / TimelinePage).
// Used to build the canonical ordered stage list from the project's activeTasks.
const DEFAULT_ID_TO_KEY = {
  1: 'advance', 2: 'cd_ad', 3: 'cd_id', 4: 'sd_ad', 5: 'sd_id',
  6: 'dd_ad', 7: 'dd_id', 8: 'aoc_ad', 9: 'aoc_id', 10: 'td_ad', 11: 'td_id',
  12: 'gfc1', 13: 'gfc2', 14: 'gfc3', 15: 'completion', 16: 'cc',
  17: 'pcd',
};

const KNOWN_STAGE_KEYS = new Set(Object.keys(STAGE_NAMES));

const LS_KEY = 'mom_dir_selectedProject';
const lsSentKey = (pid) => `mom_sent_${pid}`;
function lsLoadSent(pid) {
  try { const r = localStorage.getItem(lsSentKey(pid)); return r ? new Set(JSON.parse(r)) : new Set(); } catch { return new Set(); }
}
function lsSaveSent(pid, s) {
  try { localStorage.setItem(lsSentKey(pid), JSON.stringify([...s])); } catch { /* */ }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function isStageRow(key) {
  if (!key || typeof key !== 'string') return false;
  if (KNOWN_STAGE_KEYS.has(key)) return true;
  if (key.startsWith('misc_')) return true;
  // Part-stage header keys (substage_<mainKey>_<partId>) act as their own stage-level group.
  if (key.startsWith('substage_')) return true;
  return false;
}

// Parse a part-stage key (substage_<mainKey>_<partId>) into its main stage key + part id.
// Part ids never contain underscores, so split on the final underscore.
function parseSubStageKey(key) {
  if (!key || typeof key !== 'string' || !key.startsWith('substage_')) return null;
  const rest = key.slice('substage_'.length);
  const li = rest.lastIndexOf('_');
  if (li < 1) return null;
  return { mainKey: rest.slice(0, li), partId: rest.slice(li + 1) };
}

function getStageName(key, timeline) {
  if (STAGE_NAMES[key]) return STAGE_NAMES[key];
  const sub = parseSubStageKey(key);
  if (sub) {
    const part = (timeline?.[sub.mainKey]?.partStages || []).find(p => String(p.id) === sub.partId);
    if (part?.name) return part.name;
  }
  const data = timeline?.[key];
  if (data?.taskName) return data.taskName;
  if (key && key.startsWith('misc_')) return key.replace('misc_', 'Stage: ');
  return key || 'Stage';
}

function getTaskName(key, timeline) {
  if (!key) return 'Unknown';
  const sub = parseSubStageKey(key);
  if (sub) {
    const part = (timeline?.[sub.mainKey]?.partStages || []).find(p => String(p.id) === sub.partId);
    if (part?.name) return part.name;
  }
  const data = timeline?.[key];
  if (data?.taskName) return data.taskName;
  if (STAGE_NAMES[key]) return STAGE_NAMES[key];
  if (Array.isArray(timeline?._rows)) {
    const row = timeline._rows.find(r => (typeof r === 'string' ? r : r?.id) === key);
    const label = typeof row === 'object' ? row?.defaultLabel : null;
    if (label) return label;
  }
  if (key.startsWith('closure_')) return 'Closure';
  if (key.startsWith('invoicing_')) return 'Invoicing';
  if (key.startsWith('deliv_')) return 'Deliverable';
  return key;
}

// Walk _rows to find which stage a task_key belongs to.
// For meeting_ rows, extract the stage directly from the key pattern — this
// works even when _rows hasn't been fully populated by TimelinesPage yet.
function getParentStage(taskKey, timeline) {
  if (isStageRow(taskKey)) return taskKey;
  // meeting_N_stageKey → stageKey is everything after "meeting_<digits>_"
  if (taskKey && taskKey.startsWith('meeting_')) {
    const m = taskKey.match(/^meeting_\d+_(.+)$/);
    if (m && m[1]) return m[1];
  }
  const rows = timeline?._rows;
  if (!Array.isArray(rows)) return null;
  let lastStage = null;
  for (const row of rows) {
    const rowId = typeof row === 'string' ? row : row?.id;
    if (!rowId) continue;
    if (isStageRow(rowId)) lastStage = rowId;
    if (rowId === taskKey) return lastStage;
  }
  return null;
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(d.getDate()).padStart(2,'0')}-${M[d.getMonth()]}-${d.getFullYear()}`;
}

// Insert a new Meeting N row into the timeline for a given stage.
// Returns { newTimeline, newKey, label }.
function buildMeetingRow(timeline, stageKey) {
  const rawRows = Array.isArray(timeline?._rows) ? timeline._rows : [];
  const rows = rawRows.map(r => (typeof r === 'string' ? { id: r, indent: 1 } : r));

  // If a part-stage key is passed, the meeting belongs to that part: it is created under the
  // main stage and tagged with the part id (via _subStageId) so it groups under the part header.
  const sub = parseSubStageKey(stageKey);
  const partId = sub ? sub.partId : null;
  const baseStageKey = sub ? sub.mainKey : stageKey;
  stageKey = baseStageKey;

  // Highest existing meeting number for this stage (handles gaps from deletions)
  const esc = stageKey.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`^meeting_(\\d+)_${esc}$`);
  let maxN = 0;
  for (const r of rows) {
    const m = r.id && r.id.match(pattern);
    if (m) maxN = Math.max(maxN, parseInt(m[1], 10));
  }
  const n = maxN + 1;
  const newKey = `meeting_${n}_${stageKey}`;
  const newRow = { id: newKey, indent: 1, defaultLabel: `Meeting ${n}` };

  // Insertion point: after the last non-closure/invoicing row inside the stage
  let insertAfterIdx = -1;
  let inStage = false;
  for (let i = 0; i < rows.length; i++) {
    const id = rows[i].id;
    if (id === stageKey) { inStage = true; insertAfterIdx = i; continue; }
    if (inStage) {
      const indent = rows[i].indent ?? 1;
      if (indent === 0) break; // hit the next stage
      if (!id.startsWith('closure_') && !id.startsWith('invoicing_')) {
        insertAfterIdx = i;
      }
    }
  }

  const newRows = [...rows];
  if (insertAfterIdx === -1) {
    // Stage row not found in _rows — prepend it so TimelinesPage can reconcile correctly
    newRows.unshift({ id: stageKey, indent: 0 });
    insertAfterIdx = 0;
  }
  newRows.splice(insertAfterIdx + 1, 0, newRow);
  const newTimeline = { ...timeline, _rows: newRows };
  // Tag the new meeting row with its part so it groups under the part header in TimelinesPage too.
  if (partId) newTimeline[newKey] = { ...(newTimeline[newKey] || {}), _subStageId: partId };
  return { newTimeline, newKey, label: `Meeting ${n}`, partId };
}

// ── Standalone PDF export for a stored MOM document ─────────────────────────
function exportDocPDF(doc, headerImageUrl) {
  const metaMatch = (doc.content_html || '').match(/^<!--MOM_META:([\s\S]*?)-->\n?/);
  let meta = {};
  if (metaMatch) { try { meta = JSON.parse(metaMatch[1]); } catch { /* ignore */ } }
  const bodyHtml = (doc.content_html || '').replace(/^<!--MOM_META:[\s\S]*?-->\n?/, '');
  const title = doc.title || 'Meeting Notes';
  const raw = (s) => String(s || '');
  const attendeeRows = (meta.attendees || []).map(a => {
    const lb = raw(a.label); const val = raw(a.value);
    const labelText = lb.replace(/<[^>]+>/g, '').trim();
    return `<p style="margin:0 0 2px"><span style="font-weight:300;">${lb}${labelText ? ':' : ''}</span> <span style="font-weight:600;">${val}</span></p>`;
  }).join('');
  const metaHtml = `<div style="font-size:12px;font-family:Raleway,sans-serif;font-weight:300;line-height:1.8;margin-bottom:18px;">
    <div style="text-align:right;font-size:12px;font-weight:300;letter-spacing:0.15em;color:#444;margin-bottom:14px;">M.O.M</div>
    <p style="margin:0 0 2px"><span style="font-weight:400;">${raw(meta.dateLabel) || 'Date:'}</span> <span style="font-weight:600;">${raw(meta.date)}</span></p>
    <p style="margin:0 0 13px"><span style="font-weight:400;">${raw(meta.projectLabel) || 'Project:'}</span> <span style="font-weight:600;">${raw(meta.project)}</span></p>
    <p style="margin:0 0 2px;font-weight:400;">${raw(meta.attendeesLabel) || 'Attendees:'}</p>
    ${attendeeRows}
    <p style="margin:3px 0 0;text-align:center"><span style="font-weight:600;">${raw(meta.subjectLabel) || 'Subject:'} ${raw(meta.subject)}</span></p>
  </div>`;
  const css = `.mom-editor{font-family:Raleway,sans-serif;font-size:12px;font-weight:300;line-height:1.7;color:#222;}
    .mom-editor h1{font-size:20px;font-weight:700;margin:18px 0 10px;}.mom-editor h2{font-size:16px;font-weight:600;margin:14px 0 8px;}
    .mom-editor h3{font-size:14px;font-weight:600;margin:12px 0 6px;}.mom-editor p{margin:4px 0;}
    .mom-editor table{border-collapse:collapse;width:100%;margin:10px 0 14px;font-size:11px;}
    .mom-editor th,.mom-editor td{border:1px solid #d1d5db;padding:4px 8px;text-align:left;}
    .mom-editor ul,.mom-editor ol{padding-left:26px;margin:4px 0 6px;}.mom-editor li{margin-bottom:3px;line-height:1.6;}`;
  const hdrHtml = headerImageUrl
    ? `<div style="margin:0;padding:0;line-height:0"><img src="${headerImageUrl}" style="width:100%;height:auto;display:block;" /></div>`
    : '';
  const contentPad = headerImageUrl ? '28px 40px 72px' : '40px 60px 72px';
  const win = window.open('', '_blank');
  if (!win) { alert('Allow popups to export PDF.'); return; }
  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${title}</title><link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin><link href="https://fonts.googleapis.com/css2?family=Raleway:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400;1,600&display=swap" rel="stylesheet"><style>body{margin:0;background:#fff;font-family:Raleway,sans-serif;}@media print{body{margin:0;}}${css}</style></head><body>${hdrHtml}<div style="padding:${contentPad}">${metaHtml}<div class="mom-editor">${bodyHtml}</div></div><script>window.onload=function(){window.print();};<\/script></body></html>`);
  win.document.close();
}

// ── Stage group component ─────────────────────────────────────────────────────
function StageGroup({ group, timeline, onOpenDoc, onAddMOM, onAddToTimeline, onSend, sentDocs, onToggleSent, onDelete }) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    // No overflow:hidden so the "+" button can float outside the right border
    <div style={{ marginBottom: '10px', fontFamily: 'Raleway, sans-serif' }}>
      <div style={{ border: '1px solid ' + BRIO_BORDER, borderRadius: '4px' }}>
        {/* Stage header */}
        <div
          onClick={() => setCollapsed(c => !c)}
          style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '7px 12px', cursor: 'pointer',
            background: BRIO_BG, borderBottom: collapsed ? 'none' : '1px solid ' + BRIO_BORDER,
            borderRadius: collapsed ? '4px' : '4px 4px 0 0',
            userSelect: 'none',
          }}
        >
          <span style={{ color: '#444', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
            {collapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
          </span>

          {/* Stage title + notes count inline */}
          <span style={{ fontSize: '11px', fontWeight: 600, color: '#444', textTransform: 'uppercase', letterSpacing: '0.06em', flex: 1, display: 'flex', alignItems: 'center', gap: '5px' }}>
            {group.name}
            <span style={{ fontSize: '10px', fontWeight: 300, color: BRIO_GREY, textTransform: 'none', letterSpacing: 'normal' }}>
              — {group.docs.length} {group.docs.length === 1 ? 'note' : 'notes'}
            </span>
          </span>

          {/* New M.o.m button */}
          <span
            role="button"
            onClick={e => { e.stopPropagation(); onAddMOM(group); }}
            title="New M.o.m"
            style={{
              fontSize: '10px', fontWeight: 400, color: BRIO_GOLD,
              border: '1px solid rgba(159,144,42,0.4)', borderRadius: '2px',
              padding: '2px 8px', whiteSpace: 'nowrap', cursor: 'pointer',
              background: 'transparent', flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(159,144,42,0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            New M.o.m
          </span>
        </div>

        {/* Doc list */}
        {!collapsed && (
          <div style={{ background: '#fff', borderRadius: '0 0 4px 4px' }}>
            {group.docs.map((doc, i) => {
              const isUnlinked = isStageRow(doc.task_key);
              const taskName = getTaskName(doc.task_key, timeline);
              const title = doc.title?.trim() || null;
              return (
                // Relative container so the "+" can be absolutely positioned outside
                <div key={doc.id} style={{ position: 'relative' }}>
                  <div
                    onClick={() => onOpenDoc(doc)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '9px 14px',
                      borderBottom: i < group.docs.length - 1 ? '1px solid ' + BRIO_BORDER : 'none',
                      cursor: 'pointer', transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(159,144,42,0.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                  >
                    <FileText size={15} style={{ color: BRIO_GOLD, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12px', fontWeight: 500, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {title || `Meeting Notes`}
                      </div>
                      {!isUnlinked && (
                        <div style={{ fontSize: '11px', fontWeight: 300, color: BRIO_GREY, marginTop: '1px' }}>
                          {taskName !== group.name ? taskName : ''}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', fontWeight: 300, color: BRIO_GREY, flexShrink: 0, textAlign: 'right' }}>
                      <div>{fmtDate(doc.updated_at)}</div>
                      <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '1px' }}>last edited</div>
                    </div>
                    {onSend && (() => {
                      const isSent = sentDocs && sentDocs.has(doc.id);
                      return (
                        <>
                          <button
                            onClick={e => { e.stopPropagation(); if (!isSent) onSend(doc); }}
                            title={isSent ? 'Already sent' : 'Send meeting notes'}
                            disabled={isSent}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px',
                              fontFamily: 'Raleway, sans-serif', fontSize: '10px', fontWeight: 400,
                              color: isSent ? '#bbb' : BRIO_GOLD,
                              background: 'transparent',
                              border: `1px solid ${isSent ? '#e2e8f0' : 'rgba(159,144,42,0.4)'}`,
                              borderRadius: '2px',
                              padding: '2px 7px', cursor: isSent ? 'default' : 'pointer',
                              whiteSpace: 'nowrap', flexShrink: 0, marginLeft: '8px',
                              minWidth: '46px', justifyContent: 'center',
                            }}
                            onMouseEnter={e => { if (!isSent) e.currentTarget.style.background = 'rgba(159,144,42,0.08)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            {isSent ? 'Sent' : <><Send size={9} /> Send</>}
                          </button>
                          {onToggleSent && (
                            <input
                              type="checkbox"
                              checked={!!isSent}
                              onChange={e => { e.stopPropagation(); onToggleSent(doc.id); }}
                              onClick={e => e.stopPropagation()}
                              title={isSent ? 'Unmark as sent' : 'Mark as sent'}
                              style={{ cursor: 'pointer', marginLeft: '7px', accentColor: BRIO_GOLD, flexShrink: 0, width: '13px', height: '13px' }}
                            />
                          )}
                        </>
                      );
                    })()}
                    {onDelete && (
                      <button
                        onClick={e => { e.stopPropagation(); onDelete(doc); }}
                        title="Delete meeting note"
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          color: '#c0c0c0', padding: '2px', marginLeft: '6px', flexShrink: 0,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = 'var(--brio-red, #DB6B5D)'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = '#c0c0c0'; }}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>

                  {/* "+" to add to Timelines — floats outside the right border */}
                  {isUnlinked && onAddToTimeline && (
                    <span
                      role="button"
                      title="Add to Timelines"
                      onClick={e => { e.stopPropagation(); onAddToTimeline(doc); }}
                      style={{
                        position: 'absolute', right: '-28px', top: '50%', transform: 'translateY(-50%)',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: '18px', height: '18px', borderRadius: '50%',
                        border: '1px solid rgba(159,144,42,0.45)', color: BRIO_GOLD,
                        cursor: 'pointer', background: '#fff', flexShrink: 0,
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(159,144,42,0.12)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                    >
                      <Plus size={10} />
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function MOMDirectoryPage({ activeProjects, activeTasks, currentUser, projectStatusFilter, isAdmin, isSupervisor, supabaseProjects }) {
  const [selectedProjectId, setSelectedProjectId] = useState(() => {
    try { return localStorage.getItem(LS_KEY) || null; } catch { return null; }
  });

  // { taskKey, rowName, allowEdit, forceNew? } of the currently open MOM panel, or null
  const [momPanel, setMOMPanel] = useState(null);

  const [momSearch, setMomSearch] = useState('');

  // doc being sent (shows send popup)
  const [sendDoc, setSendDoc] = useState(null);

  // sent status per doc id — persisted per project in localStorage
  const [sentDocs, setSentDocs] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY);
      return saved ? lsLoadSent(saved) : new Set();
    } catch { return new Set(); }
  });

  // header image from branding settings (for PDF export)
  const { invoiceBranding } = useSettings();

  const selectProject = useCallback((id) => {
    setSelectedProjectId(id);
    setMOMPanel(null);
    setSentDocs(id ? lsLoadSent(id) : new Set());
    try { if (id) localStorage.setItem(LS_KEY, id); else localStorage.removeItem(LS_KEY); } catch { /* */ }
  }, []);

  const sortedProjects = useMemo(() => {
    if (!activeProjects) return [];
    let list = [...activeProjects];
    if (projectStatusFilter && projectStatusFilter !== 'default' && projectStatusFilter !== 'all') {
      list = list.filter(p => (p.status || 'active') === projectStatusFilter);
    }
    return list.sort((a, b) => (a.projectCode || a.name || '').localeCompare(b.projectCode || b.name || ''));
  }, [activeProjects, projectStatusFilter]);

  const selectedProject = useMemo(() =>
    selectedProjectId ? (sortedProjects.find(p => Number(p.id) === Number(selectedProjectId)) || null) : null,
    [sortedProjects, selectedProjectId]
  );

  // Derived from branding settings — used by exportDocPDF so the header image appears in PDFs
  const officeKey = selectedProject?.contractCurrency === 'EUR' ? 'rotterdam' : 'mumbai';
  const headerImageUrl = resolveBrandingImageSrc(invoiceBranding?.offices?.[officeKey]?.headerImage);

  const toggleSent = useCallback((docId) => {
    setSentDocs(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      if (selectedProjectId) lsSaveSent(selectedProjectId, next);
      return next;
    });
  }, [selectedProjectId]);

  const momHook = useMOMData(selectedProject ? Number(selectedProject.id) : null);

  // Local timeline override — set immediately after addToTimeline so stageGroups
  // can find the new meeting row before the parent prop has a chance to refresh.
  const [localTimeline, setLocalTimeline] = useState(null);
  const prevProjectId = useRef(selectedProjectId);
  if (prevProjectId.current !== selectedProjectId) {
    prevProjectId.current = selectedProjectId;
    setLocalTimeline(null);
  }

  const timeline = localTimeline || selectedProject?.stageTimeline || {};

  // Auto-cleanup orphaned meeting_ rows: rows in the timeline that have no linked MOM doc.
  // These are stale rows from the old "Add M.o.m" behavior that auto-created timeline rows.
  // Guard: skip while localTimeline is set (addToTimeline in flight) to avoid a race where
  // the parent prop updates before momHook.updateDocument completes, making the new
  // meeting row look orphaned before its doc task_key has been updated.
  useEffect(() => {
    if (!selectedProject || !supabaseProjects || momHook.loading || localTimeline !== null) return;
    const tl = selectedProject.stageTimeline || {};
    const rawRows = Array.isArray(tl._rows) ? tl._rows : [];
    const docTaskKeys = new Set(momHook.documents.map(d => d.task_key));
    const orphaned = rawRows
      .map(r => (typeof r === 'string' ? r : r?.id))
      .filter(id => id && id.startsWith('meeting_') && !docTaskKeys.has(id));
    if (orphaned.length === 0) return;
    const cleanedRows = rawRows.filter(r => {
      const id = typeof r === 'string' ? r : r?.id;
      return !orphaned.includes(id);
    });
    const base = { ...tl, _rows: cleanedRows };
    for (const id of orphaned) delete base[id];
    supabaseProjects.updateProject(Number(selectedProject.id), { stageTimeline: base }).catch(() => {});
  }, [selectedProject, momHook.documents, momHook.loading, supabaseProjects, localTimeline]);

  // Open the MOM builder for a new doc linked to the stage.
  // forceNew=true means the panel ignores any existing doc and starts blank.
  const openNewMOM = useCallback((stageKey) => {
    if (!selectedProject) return;
    setMOMPanel({ taskKey: stageKey, rowName: '', allowEdit: true, forceNew: true });
  }, [selectedProject]);

  // Link an unlinked (stage-level) MOM doc to a new meeting row in the timeline.
  const addToTimeline = useCallback(async (doc) => {
    if (!selectedProject || !supabaseProjects) return;
    const stageKey = doc.task_key;
    const tl = selectedProject.stageTimeline || {};
    const { newTimeline, newKey, label } = buildMeetingRow(tl, stageKey);
    // Label by number of MOM docs already in this stage
    const stageDocs = momHook.documents.filter(d => {
      const parent = getParentStage(d.task_key, tl) || (isStageRow(d.task_key) ? d.task_key : null);
      return parent === stageKey;
    });
    // For part-stage keys the meeting label follows the meeting number (Meeting N) from
    // buildMeetingRow; for main stages it follows the per-stage doc count.
    const labelText = parseSubStageKey(stageKey) ? label : `Meeting ${stageDocs.length}`;
    const fixedRows = (newTimeline._rows || []).map(r =>
      r.id === newKey ? { ...r, defaultLabel: labelText } : r
    );
    const finalTimeline = { ...newTimeline, _rows: fixedRows };
    // Update local timeline immediately so stageGroups can resolve the new row
    // before the parent prop re-renders with the updated project data.
    setLocalTimeline(finalTimeline);
    // Compute which keys were added/changed and merge only those.
    const old = selectedProject.stageTimeline || {};
    const partial = {};
    for (const k of Object.keys(finalTimeline)) { if (finalTimeline[k] !== old[k]) partial[k] = finalTimeline[k]; }
    await supabaseProjects.mergeProjectTimeline(Number(selectedProject.id), partial);
    await momHook.updateDocument(doc.id, { taskKey: newKey });
    // Both saves done — parent prop has updated, safe to release the local override
    setLocalTimeline(null);
    setMOMPanel(prev => (prev?.taskKey === stageKey ? null : prev));
  }, [selectedProject, supabaseProjects, momHook]);

  // Canonical ordered stage list for the project — mirrors TimelinesPage/TimelinePage.
  // Uses the identical sort + key-mapping logic as TimelinePage so the same stages
  // appear in the same sequence in both places.
  const sortedStages = useMemo(() => {
    // Map a task name to a known timeline key (mirrors TimelinePage.taskNameToTimelineKey)
    const taskNameToTimelineKey = (name) => {
      const n = String(name || '').trim().toLowerCase();
      if (!n) return null;
      if (n === 'pcd' || n.includes('preconcept') || n.includes('pre-concept') || n.includes('pre concept') || n.includes('masterplan')) return 'pcd';
      return null;
    };
    if (!activeTasks || activeTasks.length === 0) {
      return Object.entries(DEFAULT_ID_TO_KEY).map(([id, key]) => ({
        key,
        name: STAGE_NAMES[key] || key.replace(/_/g, ' ').toUpperCase(),
        id: Number(id),
      }));
    }
    return [...activeTasks]
      .sort((a, b) => {
        const oa = a.order != null ? a.order : (a.id <= 16 ? a.id : 1000 + a.id);
        const ob = b.order != null ? b.order : (b.id <= 16 ? b.id : 1000 + b.id);
        return oa - ob;
      })
      .map(t => ({
        key: DEFAULT_ID_TO_KEY[t.id] || taskNameToTimelineKey(t.name) || `custom_${t.id}`,
        name: t.name,
        id: t.id,
      }));
  }, [activeTasks]);

  // Group documents by stage. Only show stages that are allocated or have dates
  // (matching the filtering logic in TimelinePage). Custom stages present in the
  // timeline rows are appended after the standard ones, then an "Other" bucket for
  // unmatched docs.
  const stageGroups = useMemo(() => {
    const rawRows = Array.isArray(timeline._rows) ? timeline._rows : [];
    const rows = rawRows.map(r => (typeof r === 'string' ? r : r?.id)).filter(Boolean);

    const stageNameByKey = new Map(sortedStages.map(s => [s.key, s.name]));

    // Filter stages: show only those with allocation/dates or custom stages with docs
    // (matches TimelinePage filtering logic)
    const filteredStages = sortedStages.filter(stage => {
      if (stage.key === 'cc') return true; // CC always shown
      const tl = selectedProject?.stageTimeline?.[stage.key] || {};
      const b = tl.billing || {};
      const n = (v) => Number(v ?? 0) || 0;
      if (n(tl.invoicePercent) > 0) return true;
      if (n(b.percent) > 0) return true;
      if (n(b.lumpsum ?? b.lumpSum) > 0) return true;
      if (n(selectedProject?.budgetAllocation?.[stage.key]) > 0) return true;
      // Always show if any tracked dates are set
      if (tl.targetDate || tl.contractDate || tl.actualDate || tl.invoiceDate || tl.paidDate) return true;
      // Include if any part stage has allocation
      return (tl.partStages || []).some(p => {
        const pb = p.billing || {};
        return n(pb.percentOfStage) > 0 || n(pb.lumpsum ?? pb.lumpSum) > 0;
      });
    });

    // Part stages of a main stage (in stored order); each becomes its OWN header entry.
    const partsForStage = (stageKey) => {
      const ps = timeline?.[stageKey]?.partStages;
      return Array.isArray(ps) ? ps : [];
    };
    const partKeyOf = (stageKey, partId) => `substage_${stageKey}_${partId}`;
    // partGroupKey -> { mainStageKey, partId, name }
    const partMeta = new Map();

    const orderedStages = [];
    const seenStages = new Set();
    // Push a main stage followed by its part stages as separate (sibling) header entries.
    const pushStage = (stageKey) => {
      if (!seenStages.has(stageKey)) { orderedStages.push(stageKey); seenStages.add(stageKey); }
      for (const p of partsForStage(stageKey)) {
        const pk = partKeyOf(stageKey, p.id);
        if (!seenStages.has(pk)) {
          orderedStages.push(pk);
          seenStages.add(pk);
          partMeta.set(pk, { mainStageKey: stageKey, partId: p.id, name: p.name || 'Part' });
        }
      }
    };
    // 1. Filtered standard stages (+ their parts) for this project, in canonical order
    for (const s of filteredStages) pushStage(s.key);
    // 2. Any stage rows present in the timeline not already listed (custom stages)
    for (const rowId of rows) {
      if (isStageRow(rowId) && !rowId.startsWith('substage_') && !seenStages.has(rowId)) pushStage(rowId);
    }

    const groups = {};
    const resolveName = (key) =>
      partMeta.get(key)?.name || stageNameByKey.get(key) || getStageName(key, timeline);
    const makeGroup = (key, name) => {
      const meta = partMeta.get(key);
      return meta
        ? { key, name: name || meta.name, docs: [], isPart: true, mainStageKey: meta.mainStageKey, partId: meta.partId }
        : { key, name: name || resolveName(key), docs: [] };
    };
    // Pre-create an (empty) group for every ordered stage / part so it renders regardless of doc count
    for (const stageKey of orderedStages) {
      groups[stageKey] = makeGroup(stageKey);
    }

    // Resolve which group a doc belongs to: a part group when the linked task is assigned to a
    // sub-stage (task `_subStageId`) or when the doc key is itself a part-stage key.
    const resolveDocGroup = (doc) => {
      const tk = doc.task_key;
      if (tk && tk.startsWith('substage_')) {
        const sub = parseSubStageKey(tk);
        const part = sub ? partsForStage(sub.mainKey).find(p => String(p.id) === sub.partId) : null;
        return { key: tk, isPart: true, mainStageKey: sub?.mainKey, partId: sub?.partId, name: part?.name };
      }
      const mainKey = getParentStage(tk, timeline) || '_other';
      if (mainKey === '_other') return { key: '_other' };
      const subId = timeline?.[tk]?._subStageId;
      if (subId) {
        const part = partsForStage(mainKey).find(p => String(p.id) === String(subId));
        return { key: partKeyOf(mainKey, subId), isPart: true, mainStageKey: mainKey, partId: subId, name: part?.name };
      }
      return { key: mainKey };
    };

    for (const doc of momHook.documents) {
      const g = resolveDocGroup(doc);
      const stageKey = g.key;
      if (!groups[stageKey]) {
        groups[stageKey] = stageKey === '_other'
          ? { key: '_other', name: 'Other', docs: [] }
          : (g.isPart
              ? { key: stageKey, name: g.name || partMeta.get(stageKey)?.name || resolveName(stageKey), docs: [], isPart: true, mainStageKey: g.mainStageKey, partId: g.partId }
              : makeGroup(stageKey));
        if (stageKey !== '_other' && !orderedStages.includes(stageKey)) {
          // Keep a part group adjacent to its main stage; otherwise append.
          const mainIdx = g.mainStageKey ? orderedStages.indexOf(g.mainStageKey) : -1;
          if (mainIdx >= 0) orderedStages.splice(mainIdx + 1, 0, stageKey);
          else orderedStages.push(stageKey);
        }
      }
      groups[stageKey].docs.push(doc);
    }

    // Sort by the canonical stage sequence (sortedStages); part groups inherit their main
    // stage's rank plus a small per-part offset so they sit right after it.
    const orderIndex = new Map(sortedStages.map((s, i) => [s.key, i]));
    const fallbackBase = sortedStages.length;
    const rankOf = (key) => {
      const grp = groups[key];
      if (grp?.isPart && grp.mainStageKey) {
        const mainRank = orderIndex.has(grp.mainStageKey)
          ? orderIndex.get(grp.mainStageKey)
          : fallbackBase + orderedStages.indexOf(grp.mainStageKey);
        const parts = partsForStage(grp.mainStageKey);
        const pIdx = parts.findIndex(p => String(p.id) === String(grp.partId));
        return mainRank + 0.001 * (pIdx >= 0 ? pIdx + 1 : 1);
      }
      return orderIndex.has(key) ? orderIndex.get(key) : fallbackBase + orderedStages.indexOf(key);
    };
    const result = orderedStages
      .filter(k => groups[k])
      .sort((a, b) => rankOf(a) - rankOf(b))
      .map(k => groups[k]);
    if (groups['_other']?.docs.length > 0) result.push(groups['_other']);
    return result;
  }, [timeline, momHook.documents, sortedStages, selectedProject]);

  // Delete a meeting note from the list. Orphaned meeting_ timeline rows (if the
  // doc was linked to one) are auto-removed by the cleanup effect above.
  const deleteDoc = useCallback(async (doc) => {
    if (!doc) return;
    const label = doc.title?.trim() || 'this meeting note';
    if (!window.confirm(`Delete ${label}? This cannot be undone.`)) return;
    try {
      await momHook.deleteDocument(doc.id);
      setMOMPanel(prev => (prev && prev.taskKey === doc.task_key ? null : prev));
    } catch (e) {
      alert('Could not delete meeting note: ' + (e?.message || e));
    }
  }, [momHook]);

  const totalDocs = momHook.documents.length;

  const filteredStageGroups = useMemo(() => {
    const q = momSearch.trim().toLowerCase();
    if (!q) return stageGroups;
    return stageGroups.map(group => {
      const stageMatches = group.name.toLowerCase().includes(q);
      const matchedDocs = group.docs.filter(doc =>
        (doc.title || '').toLowerCase().includes(q)
      );
      if (!stageMatches && matchedDocs.length === 0) return null;
      return { ...group, docs: stageMatches ? group.docs : matchedDocs };
    }).filter(Boolean);
  }, [stageGroups, momSearch]);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: 'Raleway, sans-serif', minHeight: '100%', background: '#fff' }}>
      {/* Toolbar — no background fill */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        padding: '10px 16px', borderBottom: '1px solid ' + BRIO_BORDER,
        flexWrap: 'wrap',
      }}>
        {/* Project selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ fontSize: '11px', fontWeight: 500, color: BRIO_GREY, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
            Project
          </span>
          <div style={{ position: 'relative' }}>
            <select
              value={selectedProjectId || ''}
              onChange={e => selectProject(e.target.value || null)}
              style={{
                fontFamily: 'Raleway, sans-serif', fontSize: '12px', fontWeight: 400,
                padding: '4px 24px 4px 8px', border: '1px solid ' + BRIO_BORDER,
                borderRadius: '3px', background: '#fff', color: selectedProjectId ? '#333' : BRIO_GREY,
                cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none',
                minWidth: '200px', maxWidth: '320px',
              }}
            >
              <option value="">— select project —</option>
              {sortedProjects.map(p => (
                <option key={p.id} value={p.id}>
                  {p.projectCode ? `${p.projectCode} — ${p.name}` : p.name}
                </option>
              ))}
            </select>
            <ChevronDown size={12} style={{ position: 'absolute', right: '7px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: BRIO_GREY }} />
          </div>
        </div>

        {/* Doc count */}
        {selectedProject && !momHook.loading && (
          <span style={{ fontSize: '11px', fontWeight: 300, color: BRIO_GREY }}>
            {totalDocs} {totalDocs === 1 ? 'note' : 'notes'}
          </span>
        )}
        {momHook.loading && (
          <span style={{ fontSize: '11px', fontWeight: 300, color: BRIO_GREY }}>Loading…</span>
        )}

        <div style={{ flex: 1 }} />

        {/* Search */}
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <Search size={12} style={{ position: 'absolute', left: '8px', color: BRIO_GREY, pointerEvents: 'none' }} />
          <input
            type="text"
            value={momSearch}
            onChange={e => setMomSearch(e.target.value)}
            placeholder="Search meeting notes…"
            style={{
              fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 300,
              padding: '4px 10px 4px 26px', border: '1px solid ' + BRIO_BORDER,
              borderRadius: '3px', background: '#fff', color: '#333', outline: 'none',
              width: '200px',
            }}
          />
        </div>
      </div>

      {/* Body — centered, with right padding to accommodate the floating "+" buttons */}
      <div style={{ padding: '16px 48px 16px 20px', maxWidth: '948px', margin: '0 auto' }}>
        {!selectedProject ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <FileText size={32} style={{ color: BRIO_BORDER, marginBottom: '12px' }} />
            <p style={{ fontSize: '13px', fontWeight: 300, color: BRIO_GREY, margin: 0 }}>
              Select a project to browse its meeting notes.
            </p>
          </div>
        ) : momHook.loading ? (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <p style={{ fontSize: '13px', fontWeight: 300, color: BRIO_GREY, margin: 0 }}>Loading meeting notes…</p>
          </div>
        ) : filteredStageGroups.length > 0 ? (
          <div>
            {filteredStageGroups.map(group => (
              <StageGroup
                key={group.key}
                group={group}
                timeline={timeline}
                onOpenDoc={doc => setMOMPanel({ taskKey: doc.task_key, rowName: getTaskName(doc.task_key, timeline), allowEdit: true })}
                onAddMOM={group => openNewMOM(group.key)}
                onAddToTimeline={addToTimeline}
                onSend={setSendDoc}
                sentDocs={sentDocs}
                onToggleSent={toggleSent}
                onDelete={deleteDoc}
              />
            ))}
          </div>
        ) : (
          <div style={{ padding: '48px 0', textAlign: 'center' }}>
            <FileText size={32} style={{ color: BRIO_BORDER, marginBottom: '12px' }} />
            {momSearch.trim() ? (
              <p style={{ fontSize: '13px', fontWeight: 300, color: BRIO_GREY, margin: 0 }}>
                No meeting notes match <strong style={{ fontWeight: 500 }}>"{momSearch.trim()}"</strong>.
              </p>
            ) : (
              <>
                <p style={{ fontSize: '13px', fontWeight: 300, color: BRIO_GREY, margin: 0 }}>
                  No meeting notes found for <strong style={{ fontWeight: 500 }}>{selectedProject.name}</strong>.
                </p>
                <p style={{ fontSize: '12px', fontWeight: 300, color: '#aaa', marginTop: '6px' }}>
                  Use the <strong style={{ fontWeight: 500 }}>New M.o.m</strong> button on a stage group to create one.
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Send popup */}
      {sendDoc && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 10200, background: 'rgba(0,0,0,0.3)' }} onClick={() => setSendDoc(null)} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', zIndex: 10201, background: '#fff', borderRadius: '6px', padding: '28px 28px 22px', width: '340px', boxShadow: '0 12px 40px rgba(0,0,0,0.24)', fontFamily: 'Raleway, sans-serif' }}>
            <div style={{ fontWeight: 600, fontSize: '14px', color: '#222', marginBottom: '6px' }}>Send Meeting Notes</div>
            <div style={{ fontSize: '11px', fontWeight: 300, color: BRIO_GREY, marginBottom: '20px', lineHeight: 1.6 }}>
              Download a PDF of this document, then open your email app to attach it.
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <button
                onClick={() => { exportDocPDF(sendDoc, headerImageUrl); setSendDoc(null); }}
                style={{ flex: 1, fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 500, padding: '7px 12px', background: BRIO_GOLD, color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>
                Download PDF
              </button>
              <button
                onClick={() => {
                  const subj = encodeURIComponent('Minutes of Meeting: ' + (sendDoc.title || ''));
                  window.open(`mailto:?subject=${subj}`, '_self');
                  setSendDoc(null);
                }}
                style={{ flex: 1, fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 400, padding: '7px 12px', background: '#fff', color: '#444', border: '1px solid ' + BRIO_BORDER, borderRadius: '3px', cursor: 'pointer' }}>
                Open Email App
              </button>
            </div>
            <button onClick={() => setSendDoc(null)}
              style={{ fontFamily: 'Raleway, sans-serif', fontSize: '11px', fontWeight: 300, color: BRIO_GREY, background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 0', width: '100%', textAlign: 'center' }}>
              Close
            </button>
          </div>
        </>
      )}

      {/* MOM Panel overlay */}
      {momPanel && selectedProject && (
        <MOMPanel
          projectId={Number(selectedProject.id)}
          taskKey={momPanel.taskKey}
          rowName={momPanel.rowName}
          contractCurrency={selectedProject.contractCurrency}
          forceNew={!!momPanel.forceNew}
          canEdit={momPanel.allowEdit}
          isAdmin={isAdmin}
          isSupervisor={isSupervisor}
          momHook={momHook}
          projectTasksHook={null}
          onClose={() => setMOMPanel(null)}
          onDeleteRow={async (rowKey) => {
            const tl = selectedProject.stageTimeline || {};
            const rawRows = Array.isArray(tl._rows) ? tl._rows : [];
            const newRows = rawRows.filter(r => (typeof r === 'string' ? r : r?.id) !== rowKey);
            const base = { ...tl, _rows: newRows };
            delete base[rowKey];
            await supabaseProjects.updateProject(Number(selectedProject.id), { stageTimeline: base, expectedUpdatedAt: selectedProject.updatedAt });
          }}
        />
      )}
    </div>
  );
}
